import { ChevronsUpDown, Store } from 'lucide-react';
import { publicUrl } from '@/lib/env';
import { cn } from '@/lib/utils';
import { selectActiveTenant, selectTenants, useAuthStore } from '@/stores/auth-store';
import { roleLabel } from '@/theme';
import { Avatar } from '@/components/ui/Avatar';
import { Dropdown, DropdownItem, DropdownLabel } from '@/components/ui/Dropdown';

/**
 * Selector del negocio (tenant) activo.
 *
 * Es la pieza clave del multi-tenant del lado del frontend: el `tenantId` elegido es el
 * prefijo de TODAS las query keys (`src/lib/query-keys.ts`), así que al cambiarlo las
 * pantallas vuelven a pedir datos del negocio nuevo y la caché del anterior queda aislada
 * (no hay forma de mezclar datos entre tenants).
 *
 * Un usuario puede pertenecer a varios negocios (módulo 1): por eso es un menú y no un
 * texto fijo. Con un solo negocio queda como etiqueta estática (no hay nada que elegir).
 */
export function TenantSwitcher() {
  const tenants = useAuthStore(selectTenants);
  const activeTenant = useAuthStore(selectActiveTenant);
  const setActiveTenant = useAuthStore((state) => state.setActiveTenant);

  if (!activeTenant) return null;

  const singleTenant = tenants.length <= 1;

  return (
    <Dropdown
      align="start"
      className="w-56"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          disabled={singleTenant}
          aria-haspopup={singleTenant ? undefined : 'menu'}
          aria-expanded={singleTenant ? undefined : open}
          title={activeTenant.nombreEmpresa}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-control border border-line bg-surface px-3 py-2 text-left transition-colors',
            singleTenant ? 'cursor-default' : 'hover:bg-surface-muted',
          )}
        >
          <Avatar
            name={activeTenant.nombreEmpresa}
            src={activeTenant.logoUrl ? publicUrl(activeTenant.logoUrl) : null}
            size="sm"
          />

          <span className="min-w-0 flex-1">
            <span className="block truncate text-caption font-semibold text-ink">
              {activeTenant.nombreEmpresa}
            </span>
            <span className="block truncate text-micro text-ink-faint">
              {roleLabel[activeTenant.role]}
            </span>
          </span>

          {singleTenant ? null : (
            <ChevronsUpDown size={14} className="shrink-0 text-ink-faint" />
          )}
        </button>
      )}
    >
      {({ close }) => (
        <>
          <DropdownLabel>Mis negocios</DropdownLabel>

          {tenants.map((tenant) => (
            <DropdownItem
              key={tenant.id}
              icon={<Store size={16} />}
              active={tenant.id === activeTenant.id}
              onClick={() => {
                setActiveTenant(tenant.id);
                close();
              }}
            >
              {tenant.nombreEmpresa}
            </DropdownItem>
          ))}
        </>
      )}
    </Dropdown>
  );
}
